import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  GridLegacy as Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Remove as RemoveIcon,
  Notifications as NotificationIcon,
} from '@mui/icons-material';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';

interface ScoringTier {
  id: string;
  startPosition: number;
  endPosition: number;
  points: number;
}

interface Question {
  id?: string;
  text: string;
  options: string[];
  correctAnswer: number;
  subject: string;
  difficulty: 'easy' | 'medium' | 'hard';
  solution?: string;
  questionImage?: string;
  solutionImage?: string;
  answerStartTime?: Date;
  answerEndTime?: Date;
  solutionDisplayTime?: Date;
  publishedAt?: Date;
  scoringTiers: ScoringTier[];
  isActive?: boolean;
  createdAt?: any;
}

const Questions: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [open, setOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(''); // Başarı mesajı state'i eklendi
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // Manuel bildirim state'leri
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [notificationData, setNotificationData] = useState({
    title: '',
    body: '',
    type: 'general' // general, question, advertisement
  });

  // Form state
  const [formData, setFormData] = useState<Question>({
    text: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    subject: '',
    difficulty: 'medium',
    solution: '',
    questionImage: '',
    solutionImage: '',
    answerStartTime: undefined,
    answerEndTime: undefined,
    solutionDisplayTime: undefined,
    publishedAt: undefined,
    scoringTiers: [{ id: '1', startPosition: 1, endPosition: 20, points: 10 }],
    isActive: true,
  });

  const subjects = [
    { id: 'matematik', name: 'Matematik', color: '#EF4444', icon: '🔢' },
    { id: 'fen', name: 'Fen Bilimleri', color: '#06B6D4', icon: '🧪' },
    { id: 'turkce', name: 'Türkçe', color: '#3B82F6', icon: '📚' },
    { id: 'sosyal', name: 'Sosyal Bilgiler', color: '#10B981', icon: '🌍' },
    { id: 'ingilizce', name: 'İngilizce', color: '#F59E0B', icon: '🌐' },
  ];

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'questions'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const questionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        answerStartTime: doc.data().answerStartTime?.toDate(),
        answerEndTime: doc.data().answerEndTime?.toDate(),
        solutionDisplayTime: doc.data().solutionDisplayTime?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate(),
        scoringTiers: doc.data().scoringTiers || [{ id: '1', startPosition: 1, endPosition: 20, points: 10 }],
      })) as Question[];
      setQuestions(questionsData);
      setFilteredQuestions(questionsData);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Sorular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (question?: Question) => {
    if (question) {
      setEditingQuestion(question);
      setFormData({
        ...question,
        answerStartTime: question.answerStartTime || undefined,
        answerEndTime: question.answerEndTime || undefined,
        solutionDisplayTime: question.solutionDisplayTime || undefined,
      });
    } else {
      setEditingQuestion(null);
      setFormData({
        text: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        subject: '',
        difficulty: 'medium',
        solution: '',
        questionImage: '',
        solutionImage: '',
        answerStartTime: undefined,
        answerEndTime: undefined,
        solutionDisplayTime: undefined,
        scoringTiers: [{ id: '1', startPosition: 1, endPosition: 20, points: 10 }],
        isActive: true,
      });
    }
    setOpen(true);
    setError(''); // Hata mesajını temizle
    setSuccess(''); // Başarı mesajını temizle
  };

  const handleClose = () => {
    setOpen(false);
    setEditingQuestion(null);
    setError('');
    setSuccess(''); // Başarı mesajını temizle
  };

  const handleImageUpload = async (file: File, type: 'question' | 'solution') => {
    if (!file) return;

    try {
      setUploadingImage(true);
      const imageRef = ref(storage, `images/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      if (type === 'question') {
        setFormData({ ...formData, questionImage: downloadURL });
      } else {
        setFormData({ ...formData, solutionImage: downloadURL });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Resim yüklenirken hata oluştu');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (type: 'question' | 'solution') => {
    try {
      const imageUrl = type === 'question' ? formData.questionImage : formData.solutionImage;
      if (imageUrl) {
        // Delete from Firebase Storage
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
        
        if (type === 'question') {
          setFormData({ ...formData, questionImage: '' });
        } else {
          setFormData({ ...formData, solutionImage: '' });
        }
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleScoringTierChange = (index: number, field: 'startPosition' | 'endPosition' | 'points', value: number) => {
    const newTiers = [...formData.scoringTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    
    // Kademe mantığını uygula
    if (field === 'endPosition' && index < newTiers.length - 1) {
      // Bir sonraki kademenin başlangıcını güncelle
      newTiers[index + 1].startPosition = value + 1;
    }
    
    setFormData({ ...formData, scoringTiers: newTiers });
  };

  const addScoringTier = () => {
    const lastTier = formData.scoringTiers[formData.scoringTiers.length - 1];
    const newStartPosition = lastTier ? lastTier.endPosition + 1 : 1;
    
    const newTier: ScoringTier = {
      id: Date.now().toString(),
      startPosition: newStartPosition,
      endPosition: newStartPosition + 19, // Varsayılan 20 kişilik kademe
      points: lastTier ? Math.max(0, lastTier.points - 1) : 9, // Bir önceki kademeden 1 puan az
    };
    setFormData({ ...formData, scoringTiers: [...formData.scoringTiers, newTier] });
  };

  const removeScoringTier = (index: number) => {
    if (formData.scoringTiers.length > 1) {
      const newTiers = formData.scoringTiers.filter((_, i) => i !== index);
      setFormData({ ...formData, scoringTiers: newTiers });
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      // Validation
      if (!formData.text.trim() || !formData.subject) {
        setError('Soru metni ve konu seçimi zorunludur');
        return;
      }

      if (formData.options.some(option => !option.trim())) {
        setError('Tüm seçenekler doldurulmalıdır');
        return;
      }

      if (!formData.solution?.trim()) {
        setError('Çözüm metni zorunludur');
        return;
      }

      // Puanlama kademesi validasyonu
      if (formData.scoringTiers.length === 0) {
        setError('En az bir puanlama kademesi eklenmelidir');
        return;
      }
      
      // Kademe validasyonu
      for (let i = 0; i < formData.scoringTiers.length; i++) {
        const tier = formData.scoringTiers[i];
        
        if (tier.startPosition <= 0 || tier.endPosition <= 0 || tier.points < 0) {
          setError('Tüm kademe değerleri pozitif olmalıdır');
          return;
        }
        
        if (tier.startPosition > tier.endPosition) {
          setError(`Kademe ${i + 1}: Başlangıç pozisyonu bitiş pozisyonundan büyük olamaz`);
          return;
        }
        
        if (i > 0) {
          const prevTier = formData.scoringTiers[i - 1];
          if (tier.startPosition !== prevTier.endPosition + 1) {
            setError(`Kademe ${i + 1}: Başlangıç pozisyonu bir önceki kademenin bitişinden sonraki pozisyon olmalıdır`);
            return;
          }
          
          if (tier.points >= prevTier.points) {
            setError(`Kademe ${i + 1}: Puanı bir önceki kademeden düşük olmalıdır`);
            return;
          }
        }
      }

      const questionData = {
        ...formData,
        subjectId: formData.subject,
        correctOptionId: formData.correctAnswer,
        publishedAt: formData.publishedAt || new Date(),
        createdAt: new Date(),
        scoringTiers: formData.scoringTiers.sort((a, b) => a.startPosition - b.startPosition),
      };

      if (editingQuestion) {
        await updateDoc(doc(db, 'questions', editingQuestion.id!), questionData);
      } else {
        await addDoc(collection(db, 'questions'), questionData);
      }

      await fetchQuestions();
      handleClose();
    } catch (error) {
      console.error('Error saving question:', error);
      setError('Soru kaydedilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (questionId: string) => {
    if (window.confirm('Bu soruyu silmek istediğinizden emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'questions', questionId));
        await fetchQuestions();
      } catch (error) {
        console.error('Error deleting question:', error);
        setError('Soru silinirken hata oluştu');
      }
    }
  };

  // Soru bildirimi gönderme fonksiyonu
  const sendNotification = async (questionId: string, questionText: string) => {
    try {
      setSendingNotification(true);
      
      // HTTP endpoint kullanarak bildirim gönder
      const response = await fetch('https://us-central1-lgsciyiz-e1ad8.cloudfunctions.net/sendQuestionNotificationHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId: questionId,
          title: 'Yeni Soru Yayınlandı! 📚',
          body: `Yeni bir soru yayınlandı: ${questionText.substring(0, 50)}...`,
          topic: 'all_users'
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Bildirim gönderilirken bir hata oluştu');
      }
      
      console.log('Notification sent successfully:', result);
    } catch (error) {
      console.error('Error sending notification:', error);
      setError('Bildirim gönderilirken hata oluştu');
    } finally {
      setSendingNotification(false);
    }
  };

  // Manuel bildirim gönderme fonksiyonu
  const sendManualNotification = async () => {
    try {
      setSendingNotification(true);
      
      if (!notificationData.title.trim() || !notificationData.body.trim()) {
        setError('Bildirim başlığı ve mesajı zorunludur');
        return;
      }
      
      // Öncelikle direkt Firebase Functions ile deneyelim
      try {
        const functions = getFunctions();
        const sendNotification = httpsCallable(functions, 'sendManualNotification');
      
        const result = await sendNotification({
          title: notificationData.title,
          body: notificationData.body,
          type: notificationData.type,
          topic: 'all_users'
        });
        
        console.log('Manual notification sent successfully via Firebase Functions:', result);
        setShowNotificationDialog(false);
        setNotificationData({ title: '', body: '', type: 'general' });
        setError(''); // Hata mesajını temizle
        return;
      } catch (funcError) {
        console.error('Firebase Functions error, trying HTTP endpoint:', funcError);
        // Firebase Functions başarısız olursa HTTP endpoint ile devam et
      }
      
      // HTTP endpoint kullanarak bildirim gönder
      const response = await fetch('https://us-central1-lgsciyiz-e1ad8.cloudfunctions.net/sendManualNotificationHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Access-Control-Allow-Origin': '*'
        },
        body: JSON.stringify({
          title: notificationData.title,
          body: notificationData.body,
          type: notificationData.type,
          topic: 'all_users',
          timestamp: Date.now() // Benzersiz zaman damgası ekle
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error:', response.status, errorText);
        throw new Error(`Sunucu hatası: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Manual notification sent successfully via HTTP:', result);
      
      setShowNotificationDialog(false);
      setNotificationData({ title: '', body: '', type: 'general' });
      setError(''); // Hata mesajını temizle
    } catch (error) {
      console.error('Error sending manual notification:', error);
      setError(`Bildirim gönderilirken hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    } finally {
      setSendingNotification(false);
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const formatDateTime = (date: Date | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleString('tr-TR');
  };

  const formatDateTimeInput = (date: Date | undefined) => {
    if (!date) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  // Subject filter function
  const handleSubjectFilter = (subjectId: string) => {
    setSelectedSubject(subjectId);
    if (subjectId === 'all') {
      setFilteredQuestions(questions);
    } else {
      const filtered = questions.filter(q => q.subject === subjectId);
      setFilteredQuestions(filtered);
    }
  };

  // Get questions count by subject
  const getQuestionsCountBySubject = (subjectId: string) => {
    return questions.filter(q => q.subject === subjectId).length;
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 700, 
          mb: 1,
          color: '#1E293B'
        }}>
          LGS Soru Yönetimi
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Soruları yönetin, yeni sorular ekleyin ve kullanıcılara bildirim gönderin
        </Typography>
        
        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 4,
          flexWrap: 'wrap'
        }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: '#2563EB',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.1), 0 2px 4px -1px rgba(37, 99, 235, 0.06)',
              '&:hover': {
                backgroundColor: '#1D4ED8',
                boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.1), 0 4px 6px -2px rgba(37, 99, 235, 0.05)',
              }
            }}
          >
            Yeni Soru Ekle
          </Button>
          <Button
            variant="outlined"
            startIcon={<NotificationIcon />}
            onClick={() => setShowNotificationDialog(true)}
            sx={{
              borderColor: '#64748B',
              color: '#64748B',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              borderWidth: 1.5,
              '&:hover': {
                borderColor: '#475569',
                backgroundColor: '#F1F5F9',
                borderWidth: 1.5,
              }
            }}
          >
            Bildirim Gönder
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Subject Filter Cards */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Ders Seçimi
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
                            <Card
                  onClick={() => handleSubjectFilter('all')}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: selectedSubject === 'all' ? '2px solid #2563EB' : '1px solid #E2E8F0',
                    borderRadius: 2,
                    backgroundColor: selectedSubject === 'all' ? '#F0F9FF' : 'white',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    }
                  }}
                >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" sx={{ mb: 1 }}>📚</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Tüm Dersler
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {questions.length} soru
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {subjects.map((subject) => (
            <Grid item xs={12} sm={6} md={2.4} key={subject.id}>
                              <Card
                  onClick={() => handleSubjectFilter(subject.id)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: selectedSubject === subject.id ? '2px solid #2563EB' : '1px solid #E2E8F0',
                    borderRadius: 2,
                    backgroundColor: selectedSubject === subject.id 
                      ? '#F0F9FF'
                      : 'white',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    }
                  }}
                >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ mb: 1 }}>{subject.icon}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {subject.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getQuestionsCountBySubject(subject.id)} soru
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Questions Table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {selectedSubject === 'all' 
              ? 'Tüm Sorular' 
              : `${subjects.find(s => s.id === selectedSubject)?.name} Soruları`
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredQuestions.length} soru bulundu
          </Typography>
        </Box>

        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Soru</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Konu</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Zorluk</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Yayın Tarihi</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Doğru Cevap</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQuestions.map((question) => (
                <TableRow 
                  key={question.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: '#f8f9fa',
                      transition: 'background-color 0.2s ease'
                    } 
                  }}
                >
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {question.text.length > 80 
                        ? `${question.text.substring(0, 80)}...` 
                        : question.text}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">
                        {subjects.find(s => s.id === question.subject)?.icon}
                      </Typography>
                      <Typography variant="body2">
                        {subjects.find(s => s.id === question.subject)?.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={question.difficulty === 'easy' ? 'Kolay' : 
                             question.difficulty === 'medium' ? 'Orta' : 'Zor'} 
                      size="small"
                      sx={{
                        backgroundColor: question.difficulty === 'easy' ? '#10B981' : 
                                        question.difficulty === 'medium' ? '#F59E0B' : '#EF4444',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(question.publishedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={String.fromCharCode(65 + question.correctAnswer)}
                      size="small"
                      sx={{
                        backgroundColor: '#2563EB',
                        color: 'white',
                        fontWeight: 600,
                        minWidth: 30
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={question.isActive ? 'Aktif' : 'Pasif'} 
                      size="small"
                      sx={{
                        backgroundColor: question.isActive ? '#10B981' : '#94A3B8',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        onClick={() => handleOpen(question)}
                        sx={{ 
                          color: '#2563EB',
                          '&:hover': { backgroundColor: '#F0F9FF' }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => sendNotification(question.id!, question.text)}
                        disabled={sendingNotification}
                        title="Bildirim Gönder"
                        sx={{ 
                          color: '#F59E0B',
                          '&:hover': { backgroundColor: '#FFFBEB' }
                        }}
                      >
                        <NotificationIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(question.id!)}
                        sx={{ 
                          color: '#EF4444',
                          '&:hover': { backgroundColor: '#FEF2F2' }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Form Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '90vh',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#2563EB',
          color: 'white',
          py: 3,
          px: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AddIcon sx={{ fontSize: 24 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {editingQuestion ? 'Soru Düzenle' : 'Yeni Soru Ekle'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 6, maxHeight: '70vh', overflow: 'auto' }}>
          <Grid container spacing={4}>
            {/* Basic Information */}
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                mt: 4,
                p: 3,
                backgroundColor: '#F8FAFC',
                borderRadius: 2,
                border: '1px solid #E2E8F0'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                  📝 Temel Bilgiler
                </Typography>
              </Box>
            </Grid>
            
            <Grid xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 3, mr: 1 }}>
                <InputLabel sx={{ 
                  fontWeight: 500, 
                  mb: 1, 
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: 'white',
                  px: 1
                }}>Konu Seçimi</InputLabel>
                <Select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E2E8F0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563EB',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563EB',
                    },
                  }}
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">{subject.icon}</Typography>
                        <Typography>{subject.name}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 3, ml: 1 }}>
                <InputLabel sx={{ 
                  fontWeight: 500, 
                  mb: 1, 
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: 'white',
                  px: 1
                }}>Zorluk Seviyesi</InputLabel>
                <Select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as 'easy' | 'medium' | 'hard' })}
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E2E8F0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563EB',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563EB',
                    },
                  }}
                >
                  <MenuItem value="easy">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: '#10B981' 
                      }} />
                      <Typography>Kolay</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="medium">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: '#F59E0B' 
                      }} />
                      <Typography>Orta</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="hard">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: '#EF4444' 
                      }} />
                      <Typography>Zor</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#10B981',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#10B981',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500 }}>
                    {formData.isActive ? '✅ Aktif' : '❌ Pasif'}
                  </Typography>
                }
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Question Section */}
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                p: 3,
                backgroundColor: '#F8FAFC',
                borderRadius: 2,
                border: '1px solid #E2E8F0'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                  ❓ Soru İçeriği
                </Typography>
              </Box>
            </Grid>

            <Grid xs={12}>
              <TextField
                label="Soru Metni"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                fullWidth
                multiline
                rows={4}
                required
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#2563EB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563EB',
                    },
                  },
                }}
                placeholder="Sorunuzu buraya yazın..."
              />
            </Grid>

            <Grid xs={12}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                    Soru Resmi
                  </Typography>
                  {formData.questionImage ? (
                    <Box>
                      <img 
                        src={formData.questionImage} 
                        alt="Soru resmi" 
                        style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                      />
                      <Button 
                        onClick={() => handleDeleteImage('question')}
                        color="error"
                        startIcon={<DeleteIcon />}
                        sx={{ mt: 1 }}
                      >
                        Resmi Sil
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? 'Yükleniyor...' : 'Resim Yükle'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file, 'question');
                        }}
                      />
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Options */}
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                p: 3,
                backgroundColor: '#F8FAFC',
                borderRadius: 2,
                border: '1px solid #E2E8F0'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                  🔘 Seçenekler
                </Typography>
              </Box>
            </Grid>

            {formData.options.map((option, index) => (
              <Grid xs={12} md={6} key={index}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    p: 3,
                    mb: 3,
                    mt: index >= 2 ? 4 : 0, // C ve D şıkları için üstten daha fazla boşluk
                    border: formData.correctAnswer === index ? '2px solid #10B981' : '1px solid #E2E8F0',
                    borderRadius: 2,
                    backgroundColor: formData.correctAnswer === index ? '#ECFDF5' : 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: formData.correctAnswer === index ? '#10B981' : '#2563EB',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Chip 
                      label={String.fromCharCode(65 + index)}
                      size="small"
                      sx={{
                        backgroundColor: formData.correctAnswer === index ? '#10B981' : '#2563EB',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {formData.correctAnswer === index ? '✅ Doğru Cevap' : 'Seçenek'}
                    </Typography>
                  </Box>
                  <TextField
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    required
                    placeholder={`${String.fromCharCode(65 + index)} seçeneğini yazın...`}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        '&:hover fieldset': {
                          borderColor: '#9C27B0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#9C27B0',
                        },
                      },
                    }}
                  />
                  <Button
                    onClick={() => setFormData({ ...formData, correctAnswer: index })}
                    variant={formData.correctAnswer === index ? 'contained' : 'outlined'}
                    sx={{ 
                      mt: 2,
                      backgroundColor: formData.correctAnswer === index ? '#10B981' : 'transparent',
                      color: formData.correctAnswer === index ? 'white' : '#10B981',
                      borderColor: '#10B981',
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: formData.correctAnswer === index ? '#059669' : '#ECFDF5',
                      }
                    }}
                    fullWidth
                  >
                    {formData.correctAnswer === index ? '✅ Doğru Cevap' : 'Doğru Cevap Yap'}
                  </Button>
                </Card>
              </Grid>
            ))}

            {/* Solution Section */}
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                p: 3,
                backgroundColor: '#F8FAFC',
                borderRadius: 2,
                border: '1px solid #E2E8F0'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                  💡 Çözüm
                </Typography>
              </Box>
            </Grid>

            <Grid xs={12}>
              <TextField
                label="Çözüm Açıklaması"
                value={formData.solution}
                onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                fullWidth
                multiline
                rows={4}
                required
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#2563EB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563EB',
                    },
                  },
                }}
                placeholder="Sorunun detaylı çözümünü buraya yazın..."
              />
            </Grid>

            <Grid xs={12}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                    Çözüm Resmi
                  </Typography>
                  {formData.solutionImage ? (
                    <Box>
                      <img 
                        src={formData.solutionImage} 
                        alt="Çözüm resmi" 
                        style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                      />
                      <Button 
                        onClick={() => handleDeleteImage('solution')}
                        color="error"
                        startIcon={<DeleteIcon />}
                        sx={{ mt: 1 }}
                      >
                        Resmi Sil
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? 'Yükleniyor...' : 'Resim Yükle'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file, 'solution');
                        }}
                      />
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Timing Section */}
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                p: 3,
                backgroundColor: '#F8FAFC',
                borderRadius: 2,
                border: '1px solid #E2E8F0'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                  ⏰ Zamanlama
                </Typography>
              </Box>
            </Grid>

            <Grid xs={12}>
              <TextField
                label="Yayın Tarihi (Soru ne zaman görünecek?)"
                type="datetime-local"
                value={formatDateTimeInput(formData.publishedAt)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  publishedAt: e.target.value ? new Date(e.target.value) : undefined 
                })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
                helperText="Bu tarihten önce soru mobil uygulamada görünmeyecek"
                sx={{ mb: 3 }}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <TextField
                label="Cevaplama Başlangıç Zamanı"
                type="datetime-local"
                value={formatDateTimeInput(formData.answerStartTime)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  answerStartTime: e.target.value ? new Date(e.target.value) : undefined 
                })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3, mr: 1 }}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <TextField
                label="Cevaplama Bitiş Zamanı"
                type="datetime-local"
                value={formatDateTimeInput(formData.answerEndTime)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  answerEndTime: e.target.value ? new Date(e.target.value) : undefined 
                })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3, ml: 1 }}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                label="Çözüm Gösterilme Zamanı"
                type="datetime-local"
                value={formatDateTimeInput(formData.solutionDisplayTime)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  solutionDisplayTime: e.target.value ? new Date(e.target.value) : undefined 
                })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3 }}
              />
            </Grid>

            {/* Scoring Tiers */}
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                p: 3,
                backgroundColor: '#F8FAFC',
                borderRadius: 2,
                border: '1px solid #E2E8F0'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                  🏆 Puanlama Kademeleri
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Her kademe için başlangıç ve bitiş sıralaması belirleyin. Kademeler otomatik olarak birbirini takip eder.
              </Typography>
            </Grid>

            {formData.scoringTiers.map((tier, index) => (
              <Grid xs={12} key={tier.id}>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Kademe {index + 1}
                    </Typography>
                    <Stack direction="row" spacing={3} alignItems="center">
                      <TextField
                        label="Başlangıç Sırası"
                        type="number"
                        value={tier.startPosition}
                        onChange={(e) => handleScoringTierChange(index, 'startPosition', parseInt(e.target.value) || 0)}
                        inputProps={{ min: 1 }}
                        sx={{ flex: 1 }}
                        disabled={index > 0} // İlk kademe hariç başlangıç otomatik
                      />
                      <TextField
                        label="Bitiş Sırası"
                        type="number"
                        value={tier.endPosition}
                        onChange={(e) => handleScoringTierChange(index, 'endPosition', parseInt(e.target.value) || 0)}
                        inputProps={{ min: tier.startPosition }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Puan"
                        type="number"
                        value={tier.points}
                        onChange={(e) => handleScoringTierChange(index, 'points', parseInt(e.target.value) || 0)}
                        inputProps={{ 
                          min: 0,
                          max: index > 0 ? formData.scoringTiers[index - 1].points - 1 : 100
                        }}
                        sx={{ flex: 1 }}
                      />
                      {formData.scoringTiers.length > 1 && (
                        <IconButton 
                          onClick={() => removeScoringTier(index)}
                          color="error"
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {tier.startPosition}. sıradan {tier.endPosition}. sıraya kadar olan kişiler {tier.points} puan alacak
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            <Grid xs={12}>
              <Box sx={{ mt: 3 }}>
                <Button
                  onClick={addScoringTier}
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{
                    borderColor: '#2563EB',
                    color: '#2563EB',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#1D4ED8',
                      backgroundColor: '#F0F9FF',
                    }
                  }}
                >
                  Kademe Ekle
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: '#64748B',
              color: '#64748B',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                borderColor: '#475569',
                backgroundColor: '#F1F5F9',
              }
            }}
          >
            İptal
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading}
            sx={{
              backgroundColor: '#2563EB',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.1), 0 2px 4px -1px rgba(37, 99, 235, 0.06)',
              '&:hover': {
                backgroundColor: '#1D4ED8',
                boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.1), 0 4px 6px -2px rgba(37, 99, 235, 0.05)',
              },
              '&:disabled': {
                backgroundColor: '#E2E8F0',
                color: '#94A3B8',
                boxShadow: 'none',
              }
            }}
          >
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manuel Bildirim Dialog */}
      <Dialog 
        open={showNotificationDialog} 
        onClose={() => setShowNotificationDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#2563EB',
          color: 'white',
          py: 3,
          px: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationIcon sx={{ fontSize: 24 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Manuel Bildirim Gönder
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 6 }}>
          <Grid container spacing={4}>
            <Grid xs={12}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, mt: 2, textAlign: 'center', lineHeight: 1.6, pt: 2 }}>
                Tüm kullanıcılara özel bildirim gönderin. Reklam, duyuru veya genel mesajlar için kullanabilirsiniz.
              </Typography>
            </Grid>

            <Grid xs={12}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel sx={{ 
                  fontWeight: 500, 
                  mb: 1, 
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: 'white',
                  px: 1
                }}>Bildirim Türü</InputLabel>
                <Select
                  value={notificationData.type}
                  onChange={(e) => setNotificationData({ ...notificationData, type: e.target.value })}
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E2E8F0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563EB',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563EB',
                    },
                  }}
                >
                  <MenuItem value="general">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6">📢</Typography>
                      <Typography>Genel Duyuru</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="advertisement">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6">📢</Typography>
                      <Typography>Reklam</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="question">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6">❓</Typography>
                      <Typography>Soru Duyurusu</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="reminder">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6">⏰</Typography>
                      <Typography>Hatırlatma</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <TextField
                label="Bildirim Başlığı"
                value={notificationData.title}
                onChange={(e) => setNotificationData({ ...notificationData, title: e.target.value })}
                fullWidth
                required
                placeholder="Örn: Yeni Özellik Eklendi! 🎉"
                helperText="Kullanıcıların göreceği başlık"
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#2563EB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563EB',
                    },
                  },
                }}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                label="Bildirim Mesajı"
                value={notificationData.body}
                onChange={(e) => setNotificationData({ ...notificationData, body: e.target.value })}
                fullWidth
                multiline
                rows={3}
                required
                placeholder="Örn: Uygulamamıza yeni özellikler eklendi. Hemen kontrol edin!"
                helperText="Kullanıcıların göreceği mesaj (maksimum 200 karakter)"
                inputProps={{ maxLength: 200 }}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#2563EB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563EB',
                    },
                  },
                }}
              />
            </Grid>

            <Grid xs={12}>
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2, backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD' }}>
                <Typography variant="body2" sx={{ color: '#0C4A6E' }}>
                  <strong>Bilgi:</strong> Bu bildirim tüm aktif kullanıcılara gönderilecektir. 
                  Tıklayınca uygulama ana sayfasına yönlendirilir.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setShowNotificationDialog(false)}
            variant="outlined"
            sx={{
              borderColor: '#9E9E9E',
              color: '#9E9E9E',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                borderColor: '#757575',
                backgroundColor: '#f5f5f5',
              }
            }}
          >
            İptal
          </Button>
          <Button 
            onClick={sendManualNotification} 
            variant="contained" 
            disabled={sendingNotification || !notificationData.title.trim() || !notificationData.body.trim()}
            startIcon={<NotificationIcon />}
            sx={{
              backgroundColor: '#2563EB',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.1), 0 2px 4px -1px rgba(37, 99, 235, 0.06)',
              '&:hover': {
                backgroundColor: '#1D4ED8',
                boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.1), 0 4px 6px -2px rgba(37, 99, 235, 0.05)',
              },
              '&:disabled': {
                backgroundColor: '#E2E8F0',
                color: '#94A3B8',
                boxShadow: 'none',
              }
            }}
          >
            {sendingNotification ? 'Gönderiliyor...' : 'Bildirim Gönder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Questions; 